import { Article, Close } from '@mui/icons-material';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  darken,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import proxy from '@/services/ProxyService';
import useAppStore from '@/store/app';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';

/**
 * Party Selector Component
 * A unified dropdown to select ORGANIZATIONs or PERSONs
 * @param {*} param0
 * @returns
 */
export default function ArticleSelector({
  article,
  setArticle,
  options,
  enrichData = false,
}) {
  const theme = useTheme();
  const { interfaces } = useAppStore();
  const [edit, setEdit] = useState(false);
  const [articles, setArticles] = useState(options || []);
  const [loading, setLoading] = useState(false);

  const title = article ? `${article.name}` : 'Artikel auswählen';
  const subtitle = article ? article.articleNumber : 'Klicken zum Auswählen';

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query) => {
        if (!query) {
          setArticles([]);
          return;
        }

        setLoading(true);
        if (!interfaces.weclapp) {
          toast.error('Weclapp Schnittstelle nicht konfiguriert');
          setLoading(false);
          return;
        }

        try {
          const { data } = await proxy.get(
            `/${interfaces.weclapp}/article?or-articleNumber-ilike=${encodeURIComponent(`%${query}%`)}&or-name-ilike=${encodeURIComponent(`%${query}%`)}&limit=20`,
          );
          setArticles(data?.result || []);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }, 300),
    [interfaces],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <Card
      sx={{
        p: 2,
        width: '100%',
        height: '60px',
        boxShadow: 'none',
        backgroundColor: darken(theme.palette.background.paper, 0.03),
        cursor: !edit && 'pointer',
        articleSelect: 'none',
        display: 'flex',
        flexDirection: 'row',
        gap: 1.5,
        alignItems: 'center',
        '&:hover': {
          backgroundColor:
            !edit && darken(theme.palette.background.paper, 0.06),
        },
        '&:active': {
          backgroundColor:
            !edit && darken(theme.palette.background.paper, 0.09),
        },
        overflow: 'hidden',
      }}
      onClick={() => !edit && setEdit(true)}
    >
      {!edit && (
        <>
          <Avatar
            src={article?.profilePictureUrl}
            sx={{
              width: 35,
              height: 35,
              '& img': !article?.profilePictureUrl && {
                objectFit: 'contain',
                width: '80%',
              },
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Article sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          </Avatar>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: 'subtitle1.fontSize',
                lineHeight: 1.2,
                maxWidth: '90%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={title}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.2,
                maxWidth: '90%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={subtitle}
            >
              {subtitle}
            </Typography>
          </Box>
          {article && (
            <Button
              onClick={(evt) => {
                evt.stopPropagation();
                setArticle(null);
              }}
              size="small"
              sx={{
                minWidth: '30px',
                width: '30px',
                height: '30px',
                padding: 0,
                fontSize: '16px',
              }}
            >
              <Close />
            </Button>
          )}
        </>
      )}
      {edit && (
        <Autocomplete
          open={edit}
          onOpen={() => setEdit(true)}
          noOptionsText="Suchbegriff eingeben..."
          onClose={(event, reason) => {
            if (['blur', 'escape', 'selectOption'].includes(reason))
              setEdit(false);
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              debouncedSearch(newInputValue);
            }
          }}
          onChange={async (evt, value) => {
            const isSalesBOM = value?.articleType === 'SALES_BILL_OF_MATERIAL';
            if (!enrichData || !isSalesBOM) return setArticle(value);
            setLoading(true);
            try {
              const salesBillOfMaterialItems = await Promise.all(
                value?.salesBillOfMaterialItems?.map(async (bomItem) => {
                  const { data } = await proxy.get(
                    `/${interfaces.weclapp}/article/id/${bomItem.articleId}`,
                  );
                  return {
                    title: data?.name || '',
                    description: data?.description || '',
                    articleId: bomItem.articleId,
                    articleNumber: data?.articleNumber || '',
                    type: 'STATIC',
                    quantity: Number(bomItem.quantity) || 0,
                    factor: Number(bomItem.quantity) || 1,
                  };
                }),
              );
              setArticle({ ...value, salesBillOfMaterialItems });
            } finally {
              setLoading(false);
            }
          }}
          options={articles || []}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <ListItem {...props} key={option._id}>
              <ListItemText
                primary={option.name}
                secondary={option.articleNumber}
                sx={{
                  '& .MuiListItemText-primary': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiListItemText-secondary': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </ListItem>
          )}
          filterOptions={(x) => x}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Artikel"
              autoFocus
              sx={{
                '& .MuiInputBase-input': { p: 1.5 },
                '& .MuiOutlinedInput-root': { pr: '16px !important' },
              }}
              fullWidth
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      )}
    </Card>
  );
}
